import React, { useMemo } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
  const hasFraction = Number(amount) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount).replace('Rp', 'Rp.');
};

const TransferDialog = ({ 
  open, 
  onClose, 
  periodTransfers,
  branches,
  searchFilter,
  onSearchChange,
  selectedDetail,
  onSelectDetail,
  selectedPeriod,
  onRefresh
}) => {
  // Group transfers by branch - hooks must be called before any early return
  const transfersByBranch = useMemo(() => {
    if (!open) return {};
    const grouped = {};
    periodTransfers?.details?.forEach(transfer => {
      const branchId = transfer.branch_id || 'all';
      if (!grouped[branchId]) {
        grouped[branchId] = {
          branchId,
          branchName: branchId === 'all' ? 'Multi-Cabang' : (branches?.find(b => b.id === branchId)?.name || 'Unknown'),
          purchases: { total: 0, items: [] },
          expenses: { total: 0, items: [] },
          sales: { total: 0, items: [] }
        };
      }
      
      if (transfer.type === 'PURCHASE' || transfer.type === 'PURCHASE_TO_STOCK' || transfer.type === 'IMPORT_STOCK') {
        grouped[branchId].purchases.total += transfer.amount;
        grouped[branchId].purchases.items.push(transfer);
      } else if (transfer.type === 'EXPENSE') {
        grouped[branchId].expenses.total += transfer.amount;
        grouped[branchId].expenses.items.push(transfer);
      } else if (transfer.type === 'SALE') {
        grouped[branchId].sales.total += transfer.amount;
        grouped[branchId].sales.items.push(transfer);
      }
    });
    return grouped;
  }, [open, periodTransfers?.details, branches]);

  const filteredBranches = useMemo(() => {
    return Object.values(transfersByBranch).filter(branch => 
      branch.branchName.toLowerCase().includes((searchFilter || '').toLowerCase())
    );
  }, [transfersByBranch, searchFilter]);

  // Early return after all hooks
  if (!open) return null;

  const handleCancelTransfer = async (item) => {
    const confirmMessage = item.type === 'IMPORT_STOCK'
      ? 'Yakin ingin membatalkan Import Stok ini? Penjualan di periode asal dan pembelian di periode tujuan akan dihapus.'
      : 'Yakin ingin membatalkan transfer ini? Item akan dikembalikan ke periode asal.';
    
    if (!confirm(confirmMessage)) return;

    try {
      await api.delete(`/period-transfers/${item.id}`);
      
      const successMessage = item.type === 'IMPORT_STOCK'
        ? 'Import Stok berhasil dibatalkan'
        : 'Transfer berhasil dibatalkan';
      toast.success(successMessage);
      
      onSelectDetail(null);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Failed to cancel transfer:', error);
      toast.error('Gagal membatalkan transfer: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      data-testid="transfer-dialog-overlay"
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[80vh] overflow-hidden" 
        onClick={(e) => e.stopPropagation()}
        data-testid="transfer-dialog"
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Detail Pindah Periode</h2>
              <p className="text-sm text-slate-600 mt-1">
                Total: {formatCurrency(periodTransfers?.total || 0)} dari {Object.keys(transfersByBranch).length} cabang
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
              data-testid="transfer-dialog-close"
            >
              ×
            </button>
          </div>
          {!selectedDetail && (
            <div className="mt-4">
              <input
                type="text"
                placeholder="Cari nama cabang..."
                value={searchFilter || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="transfer-search-input"
              />
            </div>
          )}
        </div>
        
        {selectedDetail ? (
          /* Detail View */
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onSelectDetail(null);
              }}
              className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
              data-testid="transfer-back-btn"
            >
              ← Kembali ke ringkasan
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {selectedDetail.branchName} - {selectedDetail.type === 'purchases' ? 'Pembelian' : selectedDetail.type === 'expenses' ? 'Biaya' : 'Penjualan'}
            </h3>
            <table className="w-full" data-testid="transfer-detail-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700">Tanggal</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700">Detail</th>
                  {selectedDetail.type === 'purchases' && (
                    <th className="px-4 py-2 text-right text-sm font-semibold text-slate-700">Berat (gr)</th>
                  )}
                  <th className="px-4 py-2 text-right text-sm font-semibold text-slate-700">Jumlah</th>
                  <th className="px-4 py-2 text-center text-sm font-semibold text-slate-700">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {selectedDetail.items.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {new Date(item.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <div>{item.entity_count} item(s)</div>
                      <div className="text-xs text-slate-500 mt-1">{item.description}</div>
                    </td>
                    {selectedDetail.type === 'purchases' && (
                      <td className="px-4 py-3 text-sm text-right text-slate-700">
                        {item.total_weight ? Math.round(item.total_weight) : '-'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-semibold text-right text-green-600">
                      {formatCurrency(item.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelTransfer(item);
                        }}
                        className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        data-testid={`transfer-delete-${item.id}`}
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100">
                <tr>
                  <td colSpan="2" className="px-4 py-3 text-right font-bold text-slate-800">Total</td>
                  {selectedDetail.type === 'purchases' && (
                    <td className="px-4 py-3 text-right font-bold text-slate-800">
                      {Math.round(selectedDetail.items.reduce((sum, i) => sum + (i.total_weight || 0), 0))} gr
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {formatCurrency(selectedDetail.items.reduce((sum, i) => sum + i.amount, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          /* Summary View - Group by Branch */
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {filteredBranches.length === 0 ? (
              <p className="text-center text-slate-500">Tidak ada data pindah periode</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="transfer-summary-table">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700">Cabang</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold text-slate-700 text-orange-700">Pembelian</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold text-slate-700 text-red-700">Biaya</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold text-slate-700 text-green-700">Penjualan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBranches.map((branch) => (
                      <tr key={branch.branchId} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-800">{branch.branchName}</td>
                        <td 
                          className={`px-4 py-3 text-sm text-right ${branch.purchases.total > 0 ? 'font-semibold text-orange-600 cursor-pointer hover:bg-orange-50' : 'text-slate-400'}`}
                          onClick={() => branch.purchases.total > 0 && onSelectDetail({ branchName: branch.branchName, type: 'purchases', items: branch.purchases.items })}
                        >
                          {branch.purchases.total > 0 ? formatCurrency(branch.purchases.total) : '-'}
                        </td>
                        <td 
                          className={`px-4 py-3 text-sm text-right ${branch.expenses.total > 0 ? 'font-semibold text-red-600 cursor-pointer hover:bg-red-50' : 'text-slate-400'}`}
                          onClick={() => branch.expenses.total > 0 && onSelectDetail({ branchName: branch.branchName, type: 'expenses', items: branch.expenses.items })}
                        >
                          {branch.expenses.total > 0 ? formatCurrency(branch.expenses.total) : '-'}
                        </td>
                        <td 
                          className={`px-4 py-3 text-sm text-right ${branch.sales.total > 0 ? 'font-semibold text-green-600 cursor-pointer hover:bg-green-50' : 'text-slate-400'}`}
                          onClick={() => branch.sales.total > 0 && onSelectDetail({ branchName: branch.branchName, type: 'sales', items: branch.sales.items })}
                        >
                          {branch.sales.total > 0 ? formatCurrency(branch.sales.total) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100">
                    <tr>
                      <td className="px-4 py-3 text-left font-bold text-slate-800">Total</td>
                      <td className="px-4 py-3 text-right font-bold text-orange-600">
                        {formatCurrency(Object.values(transfersByBranch).reduce((sum, b) => sum + b.purchases.total, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">
                        {formatCurrency(Object.values(transfersByBranch).reduce((sum, b) => sum + b.expenses.total, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-600">
                        {formatCurrency(Object.values(transfersByBranch).reduce((sum, b) => sum + b.sales.total, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
        
        <div className="p-4 border-t flex justify-end">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700"
            data-testid="transfer-dialog-close-btn"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferDialog;
